/**
 * VSI.1 (Juni 2026) — Sicherheits-Symbolleiste (NUR Anzeige / unterstützend).
 *
 * Zeigt die vom Backend deterministisch erkannten Sicherheitszeichen
 * (`course.safety_symbols`) im Mitarbeiter-Unterweisungsflow an. Form/Farbe sind
 * an die Sicherheitszeichen-Norm (ISO 7010 / DGUV) angelehnt:
 *   gebot=blauer Kreis · warnung=gelbes Dreieck · verbot=roter Kreis+Balken ·
 *   brandschutz=rotes Quadrat · rettung=grünes Quadrat.
 *
 * Zusatzbedingung (Auftraggeber): Symbole wirken NUR unterstützend, niemals als
 * alleinige Pflichtinformation. Jedes Symbol wird IMMER mit Klartext-Label
 * gezeigt; maßgeblich bleibt der Unterweisungstext.
 *
 * Reine Anzeige — keine Beweiskette, kein PDF/WORM, keine Mutation.
 */
import {
  Glasses,
  Headphones,
  Hand,
  HardHat,
  Footprints,
  PersonStanding,
  Wind,
  Shirt,
  ArrowDown,
  FlaskConical,
  Flame,
  Zap,
  Waves,
  Weight,
  Thermometer,
  ChevronsLeftRight,
  User,
  FireExtinguisher,
  Plus,
  DoorOpen,
  ShieldAlert,
} from "lucide-react";

import { useResolvedT } from "@/i18n/monteur";

export const GLYPHS = {
  schutzbrille: Glasses,
  gehoerschutz: Headphones,
  schutzhandschuhe: Hand,
  schutzhelm: HardHat,
  sicherheitsschuhe: Footprints,
  auffanggurt: PersonStanding,
  atemschutz: Wind,
  warnweste: Shirt,
  absturzgefahr: ArrowDown,
  gefahrstoffe: FlaskConical,
  brandgefahr: Flame,
  elektrische_spannung: Zap,
  rutschgefahr: Waves,
  schwere_last: Weight,
  heisse_oberflaeche: Thermometer,
  quetschgefahr: ChevronsLeftRight,
  kein_feuer: Flame,
  zutritt_verboten: User,
  feuerloescher: FireExtinguisher,
  erste_hilfe: Plus,
  notausgang: DoorOpen,
};

export function SymbolBadge({ category, Icon }) {
  const size = 48;

  if (category === "warnung") {
    return (
      <span
        className="relative inline-flex items-center justify-center"
        style={{ width: size, height: size }}
        aria-hidden="true"
      >
        <svg viewBox="0 0 48 46" className="absolute inset-0 h-full w-full">
          <polygon
            points="24,3 46,43 2,43"
            fill="#F6C700"
            stroke="#171717"
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
        </svg>
        <Icon
          className="relative text-[#171717]"
          style={{ width: 18, height: 18, marginTop: 8 }}
          strokeWidth={2.4}
        />
      </span>
    );
  }

  if (category === "verbot") {
    return (
      <span
        className="relative inline-flex items-center justify-center rounded-full bg-white"
        style={{ width: size, height: size, border: "4px solid #D8232A" }}
        aria-hidden="true"
      >
        <Icon className="text-slate-800" style={{ width: 20, height: 20 }} strokeWidth={2.2} />
        <span
          className="absolute left-1/2 top-1/2 rounded-full"
          style={{
            width: "118%",
            height: 4,
            background: "#D8232A",
            transform: "translate(-50%, -50%) rotate(45deg)",
          }}
        />
      </span>
    );
  }

  const bg =
    category === "gebot" ? "#1F5FA8" : category === "brandschutz" ? "#C62828" : "#237F52";
  const radius = category === "gebot" ? "rounded-full" : "rounded-md";
  return (
    <span
      className={`inline-flex items-center justify-center ${radius}`}
      style={{ width: size, height: size, background: bg }}
      aria-hidden="true"
    >
      <Icon className="text-white" style={{ width: 22, height: 22 }} strokeWidth={2.2} />
    </span>
  );
}

export function SafetySymbols({ symbols, className = "", lang = null }) {
  const t = useResolvedT(lang);
  if (!Array.isArray(symbols) || symbols.length === 0) return null;

  return (
    <div
      className={`rounded-lg border border-slate-200 bg-slate-50/70 p-4 ${className}`}
      data-testid="safety-symbols-bar"
    >
      <div className="flex items-start gap-2 mb-3">
        <ShieldAlert className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">
            {t("vlo.safetySymbols.title")}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            {t("vlo.safetySymbols.hint")}
          </p>
        </div>
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-4">
        {symbols.map((s) => {
          const Icon = GLYPHS[s.id];
          if (!Icon) return null;
          return (
            <li
              key={s.id}
              className="flex flex-col items-center text-center"
              style={{ width: 76 }}
              data-testid={`safety-symbol-${s.id}`}
            >
              <SymbolBadge category={s.category} Icon={Icon} />
              <span className="mt-1.5 text-[11px] leading-tight text-slate-700">{s.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
